import gql from 'graphql-tag';

// Hooks
import { useQuery } from '@apollo/react-hooks';

// Types
import { GetAllFiltersQuery, GetAllFiltersQueryVariables } from 'src/app/gql/__generated__/hooks';

// Fetches every GraphQL-sourced filter domain in a single request instead of
// one request per domain - see useLoadAllFilters in filtersActions.ts.
//
// operations.acids is deliberately not requested: nothing maps it and it
// always comes back empty. infringements.acids is only requested when a page
// will show it, because it is by far the largest field (~29k values, ~80% of
// the response on a large site) - see useLoadAllFilters.
export const GET_ALL_FILTERS = gql(`
  query getAllFilters($includeInfringementAcids: Boolean!) {
    filtersByDeployedProductId {
      operations {
        aircraftCategories
        aircraftTypes
        airlines
        airportIds
        correlated
        operationTypes
        operatorCategories
        pathnames
        remoteAirportIds
        runwayNames
      }
      reports {
        dataType
        reportFormat
        reportInterval
        templateName
      }
      modelingAircraftAssignments {
        engineModels
      }
      airtrak {
        emissions
        aircraftTypes
        engineModels
        airlines
        regions
        phases
        ltoPhases
        outBoundPhases
        inBoundPhases
      }
      infringements {
        acids @include(if: $includeInfringementAcids)
        aircraftCategories
        aircraftTypes
        airlines
        candidates
        infringementTypes
        ruleNames
        severities
        statuses
        vectored
      }
      noiseEvents {
        aircraftCategories
        classificationGroups
        classifications
        locationNames
        operatorCategories
      }
      complaints {
        disturbanceTypes
        hasOperation
        statuses
      }
      settings {
        aircraftTypes
        airlines
        daysOfWeek
        destination
        origin
      }
    }
  }
`);

export const useGetAllFilters = (skip = false, includeInfringementAcids = false) => {
  const { data, loading, error } = useQuery<GetAllFiltersQuery, GetAllFiltersQueryVariables>(
    GET_ALL_FILTERS,
    {
      skip,
      variables: { includeInfringementAcids },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      // A resolver error for one domain shouldn't take every other domain's
      // already-resolved data down with it - keep whatever data did land.
      errorPolicy: 'all',
      onError: error => {
        console.error('Error fetching filters:', error);
      },
    }
  );

  return {
    allFilters: data?.filtersByDeployedProductId,
    loading,
    error,
  };
};
